import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import { TenantHeading } from '../tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../../hooks/useTenantMetaMap';

export interface TenantAssociationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any;
  accessibleTenants: string[];
  onAssociate: (tenantCode: string) => Promise<void>;
  onDisassociate: (tenantCode: string) => Promise<void>;
  onSetEnvConfig: (tenantCode: string, env: string, config: any) => Promise<void>;
  onRemoveEnvConfig: (tenantCode: string, env: string) => Promise<void>;
}

export function TenantAssociationDialog({
  open,
  onOpenChange,
  product,
  accessibleTenants,
  onAssociate,
  onDisassociate,
  onSetEnvConfig,
  onRemoveEnvConfig,
}: TenantAssociationDialogProps) {
  const productId = product?.id;
  const associations = product?.tenantAssociations || [];
  const associatedSet = new Set(associations.map((a: any) => String(a.tenantCode).toLowerCase()));
  const tenantMetaMap = useTenantMetaMap(open);

  const isTenantOwnedProduct = product?.source === 'tenant';
  const ownerTenantCode = String(product?.ownerTenantCode || '').toLowerCase();

  const dialogTenants = useMemo(() => {
    const allowed = accessibleTenants.map((t) => String(t).toLowerCase());
    if (isTenantOwnedProduct && ownerTenantCode) {
      return allowed.filter((t) => t === ownerTenantCode);
    }
    return allowed;
  }, [accessibleTenants, isTenantOwnedProduct, ownerTenantCode]);

  const [tenantEnvs, setTenantEnvs] = useState<Record<string, string[]>>({});
  const [loadingEnvs, setLoadingEnvs] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [branchEdits, setBranchEdits] = useState<Record<string, string>>({});
  const branchDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!open || !productId) return;
    setError(null);
    setBranchEdits({});
    const loadEnvs = async (code: string) => {
      setLoadingEnvs((p) => ({ ...p, [code]: true }));
      try {
        const { options } = await apiClient.getTenantOptions(code, 'tenant-environments');
        setTenantEnvs((p) => ({ ...p, [code]: options || [] }));
      } catch {
        setTenantEnvs((p) => ({ ...p, [code]: [] }));
      } finally {
        setLoadingEnvs((p) => ({ ...p, [code]: false }));
      }
    };
    for (const code of dialogTenants) {
      void loadEnvs(code);
    }
  }, [open, productId, dialogTenants]);

  const getEnvConfig = (tenantCode: string, envName: string) => {
    const assoc = associations.find(
      (a: any) => String(a.tenantCode).toLowerCase() === tenantCode.toLowerCase(),
    );
    const envData = assoc?.environments?.[envName];
    const ep = envData?.envProducts?.find((e: any) => e.product === productId);
    return ep || { product: productId, branch: 'develop', deployPrEnv: false };
  };

  const getBranchDisplay = (tenantCode: string, envName: string) => {
    const key = `${tenantCode}:${envName}`;
    return branchEdits[key] ?? getEnvConfig(tenantCode, envName).branch ?? 'develop';
  };

  const debouncedEnvConfigChange = (tenantCode: string, envName: string, updates: { branch?: string }) => {
    const key = `${tenantCode}:${envName}`;
    if (updates.branch !== undefined) {
      setBranchEdits((p) => ({ ...p, [key]: updates.branch }));
      const existing = branchDebounceRef.current[key];
      if (existing) clearTimeout(existing);
      branchDebounceRef.current[key] = setTimeout(() => {
        delete branchDebounceRef.current[key];
        setBranchEdits((p) => {
          const { [key]: _, ...rest } = p;
          return rest;
        });
        handleEnvConfigChange(tenantCode, envName, updates);
      }, 500);
    }
  };

  const handleToggleTenant = async (code: string) => {
    const isAssociated = associatedSet.has(code);
    setBusy(code);
    setError(null);
    try {
      if (isAssociated) {
        await onDisassociate(code);
      } else {
        await onAssociate(code);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const isEnvConfigured = (tenantCode: string, envName: string) => {
    const assoc = associations.find(
      (a: any) => String(a.tenantCode).toLowerCase() === tenantCode.toLowerCase(),
    );
    return !!assoc?.environments?.[envName]?.envProducts?.some(
      (e: any) => e.product === productId,
    );
  };

  const handleToggleDeployEnv = async (tenantCode: string, envName: string, enabled: boolean) => {
    const key = `${tenantCode}:${envName}`;
    setBusy(key);
    setError(null);
    try {
      if (enabled) {
        const current = getEnvConfig(tenantCode, envName);
        await onSetEnvConfig(tenantCode, envName, {
          product: productId,
          branch: current.branch || 'develop',
          deployPrEnv: !!current.deployPrEnv,
        });
      } else {
        await onRemoveEnvConfig(tenantCode, envName);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const handleEnvConfigChange = async (
    tenantCode: string,
    envName: string,
    updates: { branch?: string; deployPrEnv?: boolean },
  ) => {
    const current = getEnvConfig(tenantCode, envName);
    const config = { ...current, ...updates };
    setBusy(`${tenantCode}:${envName}`);
    setError(null);
    try {
      await onSetEnvConfig(tenantCode, envName, config);
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  const renderEnvList = (code: string) => {
    const envs = tenantEnvs[code] || [];
    const loading = loadingEnvs[code];

    if (loading) {
      return <p className="text-sm text-gray-500 dark:text-gray-400">Loading environments...</p>;
    }
    if (envs.length === 0) {
      return <p className="text-sm text-gray-500 dark:text-gray-400">No environments configured for this tenant</p>;
    }

    return envs.map((envName) => {
      const cfg = getEnvConfig(code, envName);
      const key = `${code}:${envName}`;
      const isBusy = busy === key;
      const deployEnabled = isEnvConfigured(code, envName);

      return (
        <div
          key={envName}
          className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2 bg-gray-50 dark:bg-gray-900/50"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{envName}</span>
            {isBusy && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Saving...</span>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={deployEnabled}
              onChange={(e) => handleToggleDeployEnv(code, envName, e.target.checked)}
              disabled={isBusy}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Deploy to environment
          </label>

          {deployEnabled && (
            <div className="ml-6 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Branch</span>
                <Input
                  placeholder="develop"
                  value={getBranchDisplay(code, envName)}
                  onChange={(e) =>
                    debouncedEnvConfigChange(code, envName, {
                      branch: e.target.value || 'develop',
                    })
                  }
                  disabled={isBusy}
                  className="w-36 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={!!cfg.deployPrEnv}
                  onChange={(e) =>
                    handleEnvConfigChange(code, envName, {
                      deployPrEnv: e.target.checked,
                    })
                  }
                  disabled={isBusy}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Build PR environment on PR open
              </label>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="2xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Target Environments: {productId}
            {isTenantOwnedProduct ? (
              <Badge variant="outline" className="text-[10px]">Tenant product</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Common product</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {isTenantOwnedProduct && ownerTenantCode && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This product is defined in tenant YAML. PR deploy targets can only be configured for environments belonging to{' '}
              <span className="font-medium">{ownerTenantCode}</span>.
            </p>
          )}

          {!isTenantOwnedProduct && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Associate this product with tenants, then for each environment choose whether to deploy code there and whether to spin up PR preview environments.
            </p>
          )}

          {isTenantOwnedProduct && ownerTenantCode && dialogTenants.length === 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              You do not have access to the owning tenant ({ownerTenantCode}).
            </p>
          )}

          <div className="space-y-4">
            {dialogTenants.map((code) => {
              const isAssociated = isTenantOwnedProduct || associatedSet.has(code);
              const tenantMeta = resolveTenantMeta(tenantMetaMap, code);

              if (isTenantOwnedProduct) {
                return (
                  <div
                    key={code}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                  >
                    <TenantHeading tenant={tenantMeta} title={tenantMeta.name} subtitle={tenantMeta.code} />
                    <div className="mt-4 space-y-3">{renderEnvList(code)}</div>
                  </div>
                );
              }

              return (
                <div
                  key={code}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={isAssociated}
                      onChange={() => handleToggleTenant(code)}
                      disabled={busy === code}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <TenantHeading tenant={tenantMeta} title={tenantMeta.name} subtitle={tenantMeta.code} />
                    {busy === code && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Updating...</span>
                    )}
                  </div>
                  {isAssociated && (
                    <div className="ml-6 mt-3 space-y-3">{renderEnvList(code)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
